import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Download, Ship, Plane, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";

export default function SeguimientoCargas() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isCliente = hasRole("cliente");
  const [loading, setLoading] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [batches, setBatches] = useState([]);
  const [activeTab, setActiveTab] = useState("maritimos");
  const printRef = useRef();
  
  // Filtros
  const [filters, setFilters] = useState({
    fechaDesde: "",
    fechaHasta: "",
    estado: "all"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const batchesRes = await api.get("/batches");
      setBatches(batchesRes.data);
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Separar envíos marítimos y aéreos
  const enviosMaritimos = batches.filter(b => b.contenedor || b.bl_maritimo);
  const enviosAereos = batches.filter(b => b.awb_aereo && !b.contenedor);

  const applyFilters = (data) => {
    let filtered = [...data];
    
    if (filters.fechaDesde) {
      filtered = filtered.filter(b => b.created_at >= filters.fechaDesde);
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(b => b.created_at <= filters.fechaHasta + "T23:59:59");
    }
    if (filters.estado && filters.estado !== "all") {
      filtered = filtered.filter(b => b.estado === filters.estado);
    }
    
    return filtered;
  };

  const clearFilters = () => {
    setFilters({
      fechaDesde: "",
      fechaHasta: "",
      estado: "all"
    });
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return "-";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Formatear fecha SIN conversión de zona horaria
  // Las fechas se guardan como "YYYY-MM-DD" y deben mostrarse tal cual
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Si es formato ISO con hora (2026-01-21T00:00:00), extraer solo la fecha
    const datePart = dateStr.split('T')[0];
    // Dividir en partes sin crear objeto Date (evita conversión de zona horaria)
    const parts = datePart.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };
  
  // Generar nombre del archivo PDF
  const generatePdfFileName = () => {
    const today = new Date();
    const dateStr = today.toISOString().slice(0,10).replace(/-/g, '');
    const tipo = activeTab === "maritimos" ? "Marítimo" : "Aéreo";
    
    let filterStr = "";
    if (filters.fechaDesde || filters.fechaHasta) {
      const desde = filters.fechaDesde ? filters.fechaDesde.replace(/-/g, '').slice(0,8) : "";
      const hasta = filters.fechaHasta ? filters.fechaHasta.replace(/-/g, '').slice(0,8) : "";
      if (desde && hasta) {
        filterStr += ` ${desde}-${hasta}`;
      } else if (desde) {
        filterStr += ` desde ${desde}`;
      } else if (hasta) {
        filterStr += ` hasta ${hasta}`;
      }
    }
    if (filters.estado && filters.estado !== "all") {
      const estadoNombres = {
        pendiente: "pendientes",
        en_transito: "en tránsito",
        completado: "completados"
      };
      filterStr += ` ${estadoNombres[filters.estado] || filters.estado}`;
    }
    
    return `${dateStr} Seguimiento de cargas ${tipo}${filterStr}`.trim();
  };
  
  // Generar descripción de filtros para el PDF
  const getFilterDescription = () => {
    let desc = [];
    if (filters.fechaDesde) {
      desc.push(`Desde: ${formatDate(filters.fechaDesde)}`);
    }
    if (filters.fechaHasta) {
      desc.push(`Hasta: ${formatDate(filters.fechaHasta)}`);
    }
    if (filters.estado && filters.estado !== "all") {
      const estadoNombres = {
        pendiente: "Pendientes",
        en_transito: "En tránsito",
        completado: "Completados"
      };
      desc.push(`Estado: ${estadoNombres[filters.estado] || filters.estado}`);
    }
    return desc.length > 0 ? desc.join(" | ") : "";
  };
  
  // Crear PDF con paginación correcta
  const handleCreatePdf = async () => {
    setSavingPdf(true);
    try {
      const filename = generatePdfFileName();
      const printContent = document.getElementById("print-content");
      
      if (!printContent) {
        toast.error("No se encontró el contenido para generar el PDF");
        return;
      }
      
      const opt = {
        margin: [5, 3, 8, 3], // top, left, bottom, right en mm
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 1400
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape',
          compress: true
        },
        pagebreak: { 
          mode: ['css', 'legacy'],
          avoid: ['tr', 'td']
        }
      };
      
      // Generar PDF
      const pdf = await html2pdf().set(opt).from(printContent).toPdf().get('pdf');
      
      // Añadir numeración de páginas
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100);
        // Posición: centro de la página, abajo
        const pageWidth = pdf.internal.pageSize.getWidth();
        pdf.text(`${i}/${totalPages}`, pageWidth / 2, pdf.internal.pageSize.getHeight() - 5, { align: 'center' });
      }
      
      // Descargar
      pdf.save(`${filename}.pdf`);
      toast.success("PDF generado correctamente");
      
    } catch (error) {
      console.error("Error generando PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  const maritimosData = applyFilters(enviosMaritimos);
  const aereosData = applyFilters(enviosAereos);

  // Calcular totales
  const calcTotales = (data) => {
    return {
      totalCajas: data.reduce((sum, l) => sum + (l.bultos || 0), 0),
      totalPesoBruto: data.reduce((sum, l) => sum + (l.peso_bruto || 0), 0),
      totalPesoCtn: data.reduce((sum, l) => sum + (l.peso_contenedor || 0), 0),
      totalPesoNeto: data.reduce((sum, l) => sum + (l.peso_neto || 0), 0),
      totalPesoCajas: data.reduce((sum, l) => sum + ((l.bultos || 0) * (l.peso_cajas || 0.8)), 0),
      totalPesoNetoSinCajas: data.reduce((sum, l) => {
        const pesoCajas = (l.bultos || 0) * (l.peso_cajas || 0.8);
        return sum + ((l.peso_neto || 0) - pesoCajas);
      }, 0)
    };
  };

  const totalesMaritimos = calcTotales(maritimosData);
  const totalesAereos = calcTotales(aereosData);

  return (
    <div className="seguimiento-page space-y-4">
      {/* Header */}
      <div className="no-print flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Informes
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">SEGUIMIENTO DE CARGAS</h1>
            <p className="text-sm text-slate-500">LG GLOBAL SEAFOOD</p>
          </div>
        </div>
        <Button onClick={handleCreatePdf} disabled={savingPdf}>
          {savingPdf ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Crear PDF
            </>
          )}
        </Button>
      </div>

      {/* Filtros */}
      <Card className="no-print">
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Fecha Desde</Label>
              <Input
                type="date"
                value={filters.fechaDesde}
                onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fecha Hasta</Label>
              <Input
                type="date"
                value={filters.fechaHasta}
                onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="en_transito">En Tránsito</SelectItem>
                  <SelectItem value="recibido">Recibido</SelectItem>
                  <SelectItem value="completado">Completado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={clearFilters} className="w-full">
                <X className="w-3 h-3 mr-1" />
                Limpiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs para separar marítimo y aéreo */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="print-content">
        <TabsList className="no-print">
          <TabsTrigger value="maritimos" className="flex items-center gap-2">
            <Ship className="w-4 h-4" />
            Embarques Marítimos ({maritimosData.length})
          </TabsTrigger>
          <TabsTrigger value="aereos" className="flex items-center gap-2">
            <Plane className="w-4 h-4" />
            Embarques Aéreos ({aereosData.length})
          </TabsTrigger>
        </TabsList>

        {/* Contenedor para PDF */}
        <div id="print-content" ref={printRef}>
          {/* EMBARQUES MARÍTIMOS - Columnas según Excel EXACTO:
              Nro. | FECHA DE RECONOCIMIENTO | DESCRIPCIÓN | BL | CONTENEDOR | PRECINTOS | 
              CAJAS | PESO BRUTO | PESO CTN | PESO NETO | PESO CAJAS | PESO NETO SIN CAJAS |
              LOTE | FACTURA | CAPTURA | CERTIFICADO | DUA | SALIDA DEL BUQUE | LLEGADA ESTIMADA | OBSERVACION
          */}
          <TabsContent value="maritimos" className="mt-4">
            <div className="bg-white rounded-lg shadow p-4 print:shadow-none print:p-0">
              {/* Encabezado para PDF */}
              <div className="text-center mb-4 pb-2 border-b-2 border-black">
                <h1 className="text-lg font-bold">SEGUIMIENTO DE EMBARQUES MARÍTIMOS</h1>
                <p className="text-xs">LG GLOBAL SEAFOOD C.A.</p>
                <p className="text-xs text-slate-500">
                  Generado: {new Date().toLocaleDateString("es-ES")}
                </p>
                {getFilterDescription() && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    Filtros: {getFilterDescription()}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 mb-3 bg-blue-50 p-2 rounded no-print">
                <Ship className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-blue-800">SEGUIMIENTO DE EMBARQUES MARÍTIMOS</h2>
              </div>

              <div className="overflow-x-auto">
                <Table className="text-xs border-collapse border border-slate-300">
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      {/* Columnas en ORDEN EXACTO del Excel proporcionado */}
                      <TableHead className="font-bold text-[7px] w-6 border border-slate-300">Nro.</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '55px'}}>
                        <span className="block">FECHA DE</span>
                        <span className="block">RECONOCIMIENTO</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">DESCRIPCIÓN</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">BL</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">CONTENEDOR</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300" style={{minWidth: '70px'}}>PRECINTOS</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">CAJAS</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO BRUTO</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO CTN</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO NETO</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO CAJAS</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">LOTE</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">FACTURA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">CAPTURA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">CERTIFICADO</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">DUA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '50px'}}>
                        <span className="block">SALIDA</span>
                        <span className="block">BUQUE</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '50px'}}>
                        <span className="block">LLEGADA</span>
                        <span className="block">ESTIMADA</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">OBSERVACIÓN</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maritimosData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan="20" className="text-center py-8 text-slate-500 border border-slate-300">
                          No hay embarques marítimos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {maritimosData.map((lote, idx) => {
                          // Calcular pesos derivados
                          const pesoCajas = (lote.bultos || 0) * (lote.peso_cajas || 0.8);
                          const pesoNetoSinCajas = (lote.peso_neto || 0) - pesoCajas;
                          
                          return (
                            <TableRow key={lote.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`} style={{pageBreakInside: 'avoid'}}>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{idx + 1}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.fecha_reconocimiento || lote.created_at)}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{lote.producto_nombre || "PULPO"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.bl_maritimo || "-"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.contenedor || "-"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300 whitespace-pre-wrap" style={{minWidth: '70px'}}>{lote.precintos || "-"}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{lote.bultos || "-"}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(lote.peso_bruto)}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(lote.peso_contenedor || 0)}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(lote.peso_neto)}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(pesoCajas)}</TableCell>
                              <TableCell className="font-mono text-[7px] font-medium border border-slate-300">{lote.numero_lote}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.factura_origen || "-"}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.fecha_captura)}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.certificado_exportacion || "-"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.dua || "-"}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.salida_buque)}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.llegada_estimada)}</TableCell>
                              <TableCell className="text-[7px] whitespace-pre-wrap min-w-[150px] border border-slate-300">{lote.observaciones || "-"}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Fila de totales */}
                        <TableRow className="bg-slate-800 text-white font-bold">
                          <TableCell colSpan="6" className="text-[7px] border border-slate-300">TOTALES</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{totalesMaritimos.totalCajas}</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesMaritimos.totalPesoBruto)}</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesMaritimos.totalPesoCtn)}</TableCell>
                        <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesMaritimos.totalPesoNeto)}</TableCell>
                        <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesMaritimos.totalPesoCajas)}</TableCell>
                        <TableCell colSpan="7" className="border border-slate-300"></TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          </TabsContent>

          {/* EMBARQUES AÉREOS - Columnas similares pero con GUÍA/AWB en lugar de BL/CONTENEDOR */}
          <TabsContent value="aereos" className="mt-4">
            <div className="bg-white rounded-lg shadow p-4 print:shadow-none print:p-0">
              {/* Encabezado para PDF */}
              <div className="text-center mb-4 pb-2 border-b-2 border-black">
                <h1 className="text-lg font-bold">SEGUIMIENTO DE EMBARQUES AÉREOS</h1>
                <p className="text-xs">LG GLOBAL SEAFOOD C.A.</p>
                <p className="text-xs text-slate-500">
                  Generado: {new Date().toLocaleDateString("es-ES")}
                </p>
                {getFilterDescription() && (
                  <p className="text-xs text-purple-600 mt-1 font-medium">
                    Filtros: {getFilterDescription()}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 mb-3 bg-purple-50 p-2 rounded no-print">
                <Plane className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-bold text-purple-800">SEGUIMIENTO DE EMBARQUES AÉREOS</h2>
              </div>

              <div className="overflow-x-auto">
                <Table className="text-xs border-collapse border border-slate-300">
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="font-bold text-[7px] w-6 border border-slate-300">Nro.</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '55px'}}>
                        <span className="block">FECHA DE</span>
                        <span className="block">RECONOCIMIENTO</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">DESCRIPCIÓN</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">GUÍA AWB</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">CAVAS</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO BRUTO</TableHead>
                      <TableHead className="font-bold text-[7px] text-right border border-slate-300">PESO NETO</TableHead>
                      {/* PESO NETO SIN CAJAS: NO visible para clientes */}
                      {!isCliente && (
                        <TableHead className="font-bold text-[7px] text-right border border-slate-300 text-center" style={{maxWidth: '50px'}}>
                          <span className="block">PESO NETO</span>
                          <span className="block">SIN CAJAS</span>
                        </TableHead>
                      )}
                      <TableHead className="font-bold text-[7px] border border-slate-300">LOTE</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">FACTURA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">CAPTURA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">CERTIFICADO</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">DUA</TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '50px'}}>
                        <span className="block">SALIDA</span>
                        <span className="block">VUELO</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300 text-center" style={{maxWidth: '50px'}}>
                        <span className="block">LLEGADA</span>
                        <span className="block">ESTIMADA</span>
                      </TableHead>
                      <TableHead className="font-bold text-[7px] border border-slate-300">OBSERVACIÓN</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aereosData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isCliente ? 15 : 16} className="text-center py-8 text-slate-500 border border-slate-300">
                          No hay embarques aéreos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {aereosData.map((lote, idx) => {
                          const pesoCajas = (lote.bultos || 0) * (lote.peso_cajas || 0.8);
                          const pesoNetoSinCajas = (lote.peso_neto || 0) - pesoCajas;
                          
                          return (
                            <TableRow key={lote.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`} style={{pageBreakInside: 'avoid'}}>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{idx + 1}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.fecha_reconocimiento || lote.created_at)}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{lote.producto_nombre || "PULPO"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.awb_aereo || "-"}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{lote.bultos || "-"}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(lote.peso_bruto)}</TableCell>
                              <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(lote.peso_neto)}</TableCell>
                              {/* PESO NETO SIN CAJAS: NO visible para clientes */}
                              {!isCliente && (
                                <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(pesoNetoSinCajas)}</TableCell>
                              )}
                              <TableCell className="font-mono text-[7px] font-medium border border-slate-300">{lote.numero_lote}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.factura_origen || "-"}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.fecha_captura)}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.certificado_exportacion || "-"}</TableCell>
                              <TableCell className="font-mono text-[7px] border border-slate-300">{lote.dua || "-"}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.salida_buque)}</TableCell>
                              <TableCell className="text-[7px] border border-slate-300">{formatDate(lote.llegada_estimada)}</TableCell>
                              <TableCell className="text-[7px] whitespace-pre-wrap min-w-[150px] border border-slate-300">{lote.observaciones || "-"}</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Fila de totales */}
                        <TableRow className="bg-slate-800 text-white font-bold">
                          <TableCell colSpan="4" className="text-[7px] border border-slate-300">TOTALES</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{totalesAereos.totalCajas}</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesAereos.totalPesoBruto)}</TableCell>
                          <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesAereos.totalPesoNeto)}</TableCell>
                          {/* PESO NETO SIN CAJAS: NO visible para clientes */}
                          {!isCliente && (
                            <TableCell className="text-[7px] text-right font-mono border border-slate-300">{formatNumber(totalesAereos.totalPesoNetoSinCajas)}</TableCell>
                          )}
                          <TableCell colSpan={isCliente ? 7 : 8} className="border border-slate-300"></TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </div> {/* Cierre de print-content */}
      </Tabs>

      {/* Estilos para impresión horizontal */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 5mm;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Ocultar Made with Emergent y elementos no imprimibles */
          #emergent-badge,
          .no-print,
          .print-hide,
          [data-testid="app-sidebar"],
          .sidebar,
          aside,
          nav {
            display: none !important;
          }
          
          .seguimiento-page {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          #print-content {
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
          }
          
          .print-content {
            display: block !important;
            width: 100% !important;
          }
          
          /* Tabla ajustada para A4 horizontal */
          table {
            font-size: 5.5pt !important;
            width: 100% !important;
            table-layout: fixed !important;
            border-collapse: collapse !important;
          }
          
          th, td {
            padding: 1px 1.5px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            border: 0.5pt solid #94a3b8 !important;
            vertical-align: top !important;
          }
          
          th {
            font-size: 5pt !important;
            font-weight: bold !important;
            background-color: #e2e8f0 !important;
          }
          
          /* Anchos de columnas fijos para que quepan */
          th:nth-child(1), td:nth-child(1) { width: 12px !important; } /* Nro */
          th:nth-child(2), td:nth-child(2) { width: 38px !important; } /* Fecha */
          th:nth-child(3), td:nth-child(3) { width: 50px !important; } /* Descripción */
          th:nth-child(4), td:nth-child(4) { width: 48px !important; } /* BL */
          th:nth-child(5), td:nth-child(5) { width: 48px !important; } /* Contenedor */
          th:nth-child(6), td:nth-child(6) { width: 45px !important; } /* Precintos */
          th:nth-child(7), td:nth-child(7) { width: 22px !important; } /* Cajas */
          th:nth-child(8), td:nth-child(8) { width: 32px !important; } /* Peso Bruto */
          th:nth-child(9), td:nth-child(9) { width: 28px !important; } /* Peso CTN */
          th:nth-child(10), td:nth-child(10) { width: 32px !important; } /* Peso Neto */
          th:nth-child(11), td:nth-child(11) { width: 28px !important; } /* Peso Cajas */
          th:nth-child(12), td:nth-child(12) { width: 32px !important; } /* Peso Neto Sin Cajas */
          th:nth-child(13), td:nth-child(13) { width: 55px !important; } /* Lote */
          th:nth-child(14), td:nth-child(14) { width: 48px !important; } /* Factura */
          th:nth-child(15), td:nth-child(15) { width: 32px !important; } /* Captura */
          th:nth-child(16), td:nth-child(16) { width: 38px !important; } /* Certificado */
          th:nth-child(17), td:nth-child(17) { width: 28px !important; } /* DUA */
          th:nth-child(18), td:nth-child(18) { width: 32px !important; } /* Salida Buque */
          th:nth-child(19), td:nth-child(19) { width: 32px !important; } /* Llegada Est */
          th:nth-child(20), td:nth-child(20) { width: auto !important; min-width: 60px !important; } /* Observación */
          
          /* Texto en columnas de observación */
          td:last-child {
            white-space: pre-wrap !important;
            word-break: break-word !important;
          }
          
          /* Mantener colores de fondo */
          .bg-slate-800 {
            background-color: #1e293b !important;
            color: white !important;
          }
          
          .bg-slate-100, .bg-slate-50 {
            background-color: #f8fafc !important;
          }
          
          /* Evitar corte de filas entre páginas */
          tr {
            page-break-inside: avoid !important;
          }
          
          /* Encabezado del PDF */
          .text-center.mb-4 {
            margin-bottom: 8px !important;
          }
          
          .text-center.mb-4 h1 {
            font-size: 11pt !important;
            margin-bottom: 2px !important;
          }
          
          .text-center.mb-4 p {
            font-size: 7pt !important;
            margin: 1px 0 !important;
          }
        }
        
        /* Estilos específicos para el PDF generado con html2pdf */
        #print-content {
          background: white;
        }
        
        #print-content table {
          font-size: 6px;
        }
        
        #print-content th, #print-content td {
          padding: 1px 2px;
          font-size: 6px;
        }
      `}</style>
    </div>
  );
}
